/**
 * Whether the PRE-AUTH surfaces carry ThreadFlow branding, resolved from the
 * RUNTIME host.
 *
 * Card: https://trello.com/c/mq26K91V -- white-label storefront branding
 * (tier-gated), Unit 7. Spec: `docs/planning/
 * 2026-09-19-white-label-storefront-branding-plan.md`, "ADDENDUM 2".
 *
 * A DIFFERENT SURFACE CLASS FROM UNITS 1-6/8
 * ------------------------------------------
 * Those units white-label the STOREFRONT, and they key off the STORE: an
 * entitled tier plus a per-store toggle, both carried on the store payload
 * (`utils/whiteLabelMark.js`). That mechanism cannot answer this question,
 * because these are the pages a visitor crosses BEFORE any store is known --
 * there is no session, no slug, and nothing to look the store up by. The only
 * fact available at that moment is the host in the address bar.
 *
 * So this is a second resolver, and the reason it is not a second MECHANISM is
 * that it answers a different question about a different subject: "which host
 * am I on", not "what has this store bought". They are deliberately not merged;
 * a helper that took both a store and a hostname would have two independent
 * ways to say "hide the brand" and no single place that decides.
 *
 * Jake, 2026-09-20: the login page at the white-label host must be "100%
 * generic, with NO branding at all."
 *
 * WHY RUNTIME HOST AND NOT A BUILD FLAG
 * -------------------------------------
 * Both hosts serve the SAME CloudFront distribution and therefore the same
 * bundle (verified against live AWS by tf7-admin). A build-time boolean would
 * force two deployments of one artifact -- two builds, two syncs, two
 * invalidations, and a standing opportunity for the two to drift. Reading
 * `window.location.hostname` at render time means one artifact answers
 * correctly on whichever host served it.
 *
 * The host LIST is still configuration, and it is delivered the way every
 * other deploy-varying value in this SPA already is: a key in the
 * per-environment `.env` that dotenv-webpack inlines (the same path as
 * `API_BASE_URL`, `BASE_URL` and `S3_IMAGE_BASE_URL`). That is NOT the thing
 * the constraint forbids -- baking the list does not bake the DECISION. One
 * bundle still serves both hosts and behaves differently on each.
 *
 * The cost is a rebuild when Phil's domain lands, which is the cost of every
 * other value in that file. The alternative -- an unauthenticated endpoint --
 * was rejected: every route in `api/src/endpoints/platform_settings.py` is
 * `@login_required` and these surfaces have no token, so it would mean a new
 * public endpoint AND a round-trip before first paint. During that round-trip
 * the page must render something, and it would render ThreadFlow's brand,
 * which is precisely the leak this unit exists to close. A flash of the wrong
 * brand is not a smaller version of the bug; on a demo it IS the bug.
 *
 * WHY AN ALLOW-LIST, AND WHY UNKNOWN MEANS BRANDED
 * ------------------------------------------------
 * The default is BRANDED and only an explicitly configured host is bare. An
 * unset key, a typo, a new sandbox nobody added, a direct-to-ALB hostname --
 * all render ThreadFlow's brand. The asymmetry is deliberate: a missing mark
 * on a host that should have been generic is a cosmetic miss on one demo,
 * while a missing mark on `app.threadflow.com` is ThreadFlow losing its
 * identity on its own front door, silently, for everyone. Fail safe here means
 * fail BRANDED.
 *
 * Matching is EXACT on the hostname -- no suffix or wildcard match. Jake's
 * 2026-09-20 decision is path-based (`/shop/:slug`) with no per-tenant
 * hostnames, so nothing needs a wildcard; and a suffix match would hand the
 * generic treatment to any host a third party could stand up under the domain.
 *
 * ASCII-only.
 */

/**
 * The build-time configured white-label hosts, as a raw comma-separated string.
 *
 * Referenced DIRECTLY, with no `typeof process` guard. webpack's DefinePlugin
 * replaces only the `process.env.WHITE_LABEL_HOSTS` token and does NOT define
 * bare `process`, so a guard would survive into the bundle, evaluate false in
 * the browser, and strand the inlined literal on a dead branch. That exact bug
 * shipped once already and broke every uploaded image on production -- see the
 * header of `utils/imageBaseUrl.js`.
 *
 * WHY A FUNCTION AND NOT A MODULE CONSTANT. Under webpack this compiles to
 * returning a string literal either way, so the shipped behaviour is
 * identical. The difference is only visible under test: a module-level `const`
 * is frozen at import time, and since the surfaces are ROUTE components that
 * import this module transitively, a test could not configure a host before
 * that import ran.
 *
 * Module mocking does not solve it either, and the attempt is worth recording
 * because it LOOKS like it works: `vi.spyOn` and `vi.mock` both replace the
 * module's EXPORT, while `resolvePreAuthBranding` calls its own module-local
 * binding. Verified by probe -- the mocked accessor returned the test value
 * when called directly, and the resolver's internal call still saw the real
 * one. A test built on that would have asserted white-label behaviour against
 * an unconfigured build and passed while proving nothing.
 *
 * Reading at CALL time costs nothing and makes the one input a test needs to
 * state actually statable.
 *
 * @returns {?string} the raw configured value, or undefined when unset
 */
export function configuredWhiteLabelHosts() {
    return process.env.WHITE_LABEL_HOSTS
}

/**
 * dotenv-webpack's stand-in when a key is absent from `.env`.
 *
 * It emits `"MISSING_ENV_VAR".KEY`, which usually evaluates to undefined but
 * can reach runtime as the bare literal. Treated as unset rather than as a
 * hostname -- it would never match a real host either way, but reading it as
 * configuration would make a debug session chase a hostname nobody set.
 */
const MISSING_ENV_SENTINEL = 'MISSING_ENV_VAR'

/**
 * Normalize one configured entry to a bare, lowercase hostname.
 *
 * The port is stripped because the comparison is against
 * `window.location.hostname`, which never carries one. Sandboxes are reached
 * as `tf-sandbox.coules.io:8085`, so an operator copying that string into the
 * config would otherwise configure a host that can never match.
 */
function normalizeHost(value) {
    if (typeof value !== 'string') return ''
    const trimmed = value.trim().toLowerCase()
    if (!trimmed || trimmed === MISSING_ENV_SENTINEL.toLowerCase()) return ''
    // Split on the LAST colon so a bare IPv6 literal is left alone; a
    // bracketed IPv6 host keeps its brackets, matching what the browser
    // reports in `location.hostname`.
    const colon = trimmed.lastIndexOf(':')
    if (colon > trimmed.lastIndexOf(']')) return trimmed.slice(0, colon)
    return trimmed
}

/**
 * Parse the configured host list into normalized hostnames.
 *
 * Comma-separated, mirroring `CORS_EXTRA_ORIGINS` on the backend
 * (`api/src/plugins/__init__.py:31`, `api/src/services/passkey_config.py:143`)
 * -- the same operator writing the same kind of list should not have to learn
 * a second syntax for it.
 *
 * @param {?string} raw the configured value, or undefined when unset
 * @returns {string[]} normalized hostnames; empty when nothing is configured
 */
export function parseWhiteLabelHosts(raw) {
    if (typeof raw !== 'string') return []
    return raw
        .split(',')
        .map(normalizeHost)
        .filter((host) => host !== '')
}

/**
 * Is this hostname one of the configured white-label hosts?
 *
 * @param {?string} hostname typically `window.location.hostname`
 * @param {string[]|string} hosts parsed list, or the raw configured string
 * @returns {boolean} false for an unknown, empty or unconfigured host
 */
export function isWhiteLabelHost(hostname, hosts) {
    const list = Array.isArray(hosts) ? hosts : parseWhiteLabelHosts(hosts)
    if (list.length === 0) return false

    const actual = normalizeHost(hostname)
    if (!actual) return false

    return list.includes(actual)
}

/**
 * Read the browser's current hostname, tolerating a non-browser context.
 *
 * Returns '' under SSR or a bare test harness, which resolves to BRANDED --
 * the safe direction.
 */
function currentHostname() {
    if (typeof window === 'undefined' || !window.location) return ''
    return window.location.hostname || ''
}

/**
 * The pre-auth branding decision for the current request.
 *
 * ONE resolver, consumed by every pre-auth surface. Two resolvers is how two
 * pages drift apart -- and a half-applied white-label (a generic login page
 * followed by a branded password-reset page) advertises that the branding is a
 * veneer, which is worse than not doing it.
 *
 * @param {{hostname?: ?string, whiteLabelHosts?: string[]|string}} [options]
 *   Both default to the live values; the parameters exist so tests can state a
 *   host without mutating `window.location`, and so a future caller can ask
 *   about a host other than the current one.
 * @returns {{showBrand: boolean, isWhiteLabelHost: boolean, hostname: string}}
 *   `showBrand` is the only thing a template needs; the other two are returned
 *   for diagnostics and to keep a caller from re-deriving them.
 */
export function resolvePreAuthBranding(options = {}) {
    const hostname =
        options.hostname === undefined ? currentHostname() : options.hostname
    const configured =
        options.whiteLabelHosts === undefined
            ? configuredWhiteLabelHosts()
            : options.whiteLabelHosts

    const whiteLabel = isWhiteLabelHost(hostname, configured)

    return {
        showBrand: !whiteLabel,
        isWhiteLabelHost: whiteLabel,
        hostname: normalizeHost(hostname),
    }
}

export default resolvePreAuthBranding
